import { on, reducer } from "ts-action";
import { withLoadingReducer } from "../../../Components/Pickup/redux-store/reducers/withLoadingState";
import { mapById } from "../../../hooks/use-cashiers";
import { getReferralMenuAction } from "../../../constants";
import * as actions from "../../actions";
import {
    IReferralMenuCategory,
    IReferralMenuItem,
} from "../../../types/referral-gifts-types";

export interface ReferralGiftInfo {
    id: string;
    columnType: "sender" | "recipient";
    index: number;
    referral_id?: string;
}
export interface ReferralMenuState {
    categoriesIds: string[];
    shownCategoriesIds: string[];
    shownCategoriesById: Record<string, IReferralMenuCategory>;
    categoriesById: Record<string, IReferralMenuCategory>;

    senderShowCategoryIds: string[];
    senderShownCategoriesById: Record<string, IReferralMenuCategory>;

    existingGiftsMap: Map<string, ReferralGiftInfo>;
    exitingGiftsMapBackup: Map<string, ReferralGiftInfo>;

    search: string;
    loadingStatus?: string;
}

const initialState: ReferralMenuState = {
    categoriesIds: [],
    shownCategoriesIds: [],
    senderShowCategoryIds: [],
    senderShownCategoriesById: {},
    shownCategoriesById: {},
    categoriesById: {},
    existingGiftsMap: new Map(),
    exitingGiftsMapBackup: new Map(),
    search: "",
};

const removePreviousGiftsAtIndex = (
    giftsMap: Map<string, ReferralGiftInfo>,
    id: string | undefined,
    columnType: "sender" | "recipient"
): Map<string, ReferralGiftInfo> => {
    const newGiftsMap = new Map(giftsMap);

    if (id) {
        for (const [key, info] of giftsMap.entries()) {
            if (info.id === id && info.columnType === columnType) {
                newGiftsMap.delete(key);
            }
        }
    }

    return newGiftsMap;
};
const filterCategories = (
    categories: Record<string, IReferralMenuCategory>,
    searchTerm: string
): Record<string, IReferralMenuCategory> => {
    if (!searchTerm.trim()) return categories;

    const lowercaseSearch = searchTerm.toLowerCase();

    const matchesSearch = (item: IReferralMenuItem) => {
        return [item.name, item.en_name, item.ar_name].some((text) =>
            text?.toLowerCase().includes(lowercaseSearch)
        );
    };

    return Object.entries(categories).reduce(
        (filtered, [categoryId, category]) => {
            const filteredItems = category.items.filter(matchesSearch);

            if (filteredItems.length > 0) {
                filtered[categoryId] = { ...category, items: filteredItems };
            }

            return filtered;
        },
        {} as Record<string, IReferralMenuCategory>
    );
};

const filterCategoriesByGiftIds = (
    categories: Record<string, IReferralMenuCategory>,
    existingGiftsMap: Map<string, ReferralGiftInfo>
): Record<string, IReferralMenuCategory> => {
    const existingIds = new Set(existingGiftsMap.keys());

    return Object.entries(categories).reduce(
        (filtered, [categoryId, category]) => {
            const filteredItems = category.items.filter(
                (item) => !existingIds.has(item.id)
            );

            if (filteredItems.length > 0) {
                filtered[categoryId] = { ...category, items: filteredItems };
            }

            return filtered;
        },
        {} as Record<string, IReferralMenuCategory>
    );
};

const referralMenuReducer = withLoadingReducer<ReferralMenuState>(
    reducer<ReferralMenuState>(
        [
            on(actions.getReferralMenuFailure, (state) => ({
                ...state,
                categoriesIds: [],
                shownCategoriesIds: [],
                senderShowCategoryIds: [],
                categoriesById: {},
                shownCategoriesById: {},
                senderShownCategoriesById: {},
            })),

            on(actions.getReferralMenuSuccess, (state, { payload }) => {
                const categories = payload.menu || [];
                const categoriesById = mapById(categories);
                const categoriesIds = categories.map((category) => category.id);
                const filteredCategoriesById = filterCategoriesByGiftIds(
                    categoriesById,
                    state.existingGiftsMap
                );
                return {
                    ...state,
                    categoriesIds,
                    categoriesById,
                    shownCategoriesIds: Object.keys(filteredCategoriesById),
                    shownCategoriesById: filteredCategoriesById,
                    senderShowCategoryIds: categoriesIds,
                    senderShownCategoriesById: categoriesById,
                };
            }),

            on(actions.searchReferralMenuItems, (state, { payload }) => {
                const searchTerm = payload.search_term;

                if (!searchTerm) {
                    return {
                        ...state,
                        search: "",
                        shownCategoriesById: state.categoriesById,
                        shownCategoriesIds: state.categoriesIds,
                    };
                }

                let filteredCategories = filterCategories(
                    state.categoriesById,
                    searchTerm
                );
                filteredCategories = filterCategoriesByGiftIds(
                    filteredCategories,
                    state.existingGiftsMap
                );

                return {
                    ...state,
                    search: searchTerm,
                    shownCategoriesById: filteredCategories,
                    shownCategoriesIds: Object.keys(filteredCategories),
                    senderShowCategoryIds: Object.keys(
                        filterCategories(
                            state.categoriesById,
                            searchTerm
                        )
                    ),
                    senderShownCategoriesById: filterCategories(
                        state.categoriesById,
                        searchTerm
                    ),
                };
            }),
            on(actions.getReferralGiftsSuccess, (state, { payload }) => {
                const existingGiftsMap = new Map<string, ReferralGiftInfo>();

                payload.forEach((gift, index) => {
                    if (gift.recipient_referral_gift_id) {
                        existingGiftsMap.set(gift.recipient_referral_gift_id, {
                            id: gift.recipient_referral_gift_id,
                            columnType: "recipient",
                            index,
                            referral_id: gift.referral_id,
                        });
                    }
                });
                const filteredCategoriesById = filterCategoriesByGiftIds(
                    state.categoriesById,
                    existingGiftsMap
                );
                const shownCategoriesIds = Object.keys(filteredCategoriesById);
                const shownCategoriesById = filteredCategoriesById;
                return {
                    ...state,
                    existingGiftsMap,
                    exitingGiftsMapBackup: existingGiftsMap,
                    shownCategoriesIds,
                    shownCategoriesById,
                };
            }),
            on(actions.modifyReferralGiftSuccess, (state, { payload }) => {
                const newExistingGiftsMap = new Map();
                payload.forEach((gift, index) => {
                    if (gift.recipient_referral_gift_id) {
                        newExistingGiftsMap.set(gift.recipient_referral_gift_id, {
                            id: gift.recipient_referral_gift_id,
                            columnType: "recipient",
                            index,
                            referral_id: gift.referral_id,
                        });
                    }
                }
                );
                const filteredCategoriesById = filterCategoriesByGiftIds(
                    state.categoriesById,
                    newExistingGiftsMap
                );
                const shownCategoriesIds = Object.keys(filteredCategoriesById);
                const shownCategoriesById = filteredCategoriesById;
                return {
                    ...state,
                    existingGiftsMap: newExistingGiftsMap,
                    exitingGiftsMapBackup: newExistingGiftsMap,
                    shownCategoriesIds,
                    shownCategoriesById,
                };
            }),
            on(actions.modifyReferralGift, (state, { payload }) => {
                const { data, rowIndex } = payload;
                let newExistingGiftsMap = new Map(state.existingGiftsMap);
                if (data?.recipient_referral_gift_id && payload.columnType === "recipient") {
                    newExistingGiftsMap = removePreviousGiftsAtIndex(
                        newExistingGiftsMap,
                        payload.prev_recipient_referral_gift_id,
                        "recipient"
                    );
                    newExistingGiftsMap.set(data.recipient_referral_gift_id, {
                        id: data.recipient_referral_gift_id,
                        columnType: "recipient",
                        index: rowIndex,
                        referral_id: data.referral_id,
                    });
                }

                let filteredCategoriesById = filterCategoriesByGiftIds(
                    state.categoriesById,
                    newExistingGiftsMap
                );

                return {
                    ...state,
                    existingGiftsMap: newExistingGiftsMap,
                    shownCategoriesIds: Object.keys(filteredCategoriesById),
                    shownCategoriesById: filteredCategoriesById,
                };
            }),
            on(actions.modifyReferralGiftFailure, (state, { payload }) => {
                let filteredCategoriesById = filterCategoriesByGiftIds(
                    state.categoriesById,
                    state.exitingGiftsMapBackup
                );
                return {
                    ...state,
                    existingGiftsMap: state.exitingGiftsMapBackup,
                    shownCategoriesById: filteredCategoriesById,
                    shownCategoriesIds: Object.keys(filteredCategoriesById),
                }
            }),
            on(actions.deleteReferralGiftSuccess, (state, { payload }) => {
                const newExistingGiftsMap = new Map(state.existingGiftsMap);
                newExistingGiftsMap.delete(payload.sender_referral_gift_id);
                newExistingGiftsMap.delete(payload.recipient_referral_gift_id);
                let filteredCategoriesById = filterCategoriesByGiftIds(
                    state.categoriesById,
                    newExistingGiftsMap
                );
                return {
                    ...state,
                    existingGiftsMap: newExistingGiftsMap,
                    shownCategoriesById: filteredCategoriesById,
                    shownCategoriesIds: Object.keys(filteredCategoriesById),
                };
            }),
            on(actions.removeEmptyReferralGift, (state, { payload }) => {
                const newExistingGiftsMap = new Map(state.existingGiftsMap);
                const gift = payload.gift;

                if (gift.recipient_referral_gift_id) {
                    newExistingGiftsMap.delete(gift.recipient_referral_gift_id);
                }

                const filteredCategoriesById = filterCategoriesByGiftIds(
                    state.categoriesById,
                    newExistingGiftsMap
                );

                return {
                    ...state,
                    existingGiftsMap: newExistingGiftsMap,
                    shownCategoriesById: filteredCategoriesById,
                    shownCategoriesIds: Object.keys(filteredCategoriesById),
                };
            }),
        ],
        initialState
    ),
    getReferralMenuAction
);

export default referralMenuReducer;
